import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { TableCell, TableHead, TableRow, TableSortLabel, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { Children, isValidElement, ReactElement, ReactNode } from 'react';
import {
  DatagridClasses,
  FieldTitle,
  SortPayload,
  useListContextWithProps,
  useResourceContext,
  useTranslate,
} from 'react-admin';

// The two columns wired to a real source that dataProvider.ts's SORTABLE_LIST_FIELDS allowlist
// forwards to the API — keep this in sync with that Set (symbol, volume24h). The other six columns
// on this Datagrid pass sortable={false} and get PlainHeaderCell below: same content react-admin's
// own DatagridHeaderCell would render for a non-sortable field (just a FieldTitle), rebuilt locally
// rather than reused — see the note on PlainHeaderCell for why.
const SORTABLE_HEADER_SOURCES = new Set(['symbol', 'volume24h']);

// react-admin's own default sort before any column is picked. dataProvider.ts's
// SORTABLE_LIST_FIELDS allowlist only forwards sortBy/sortDirection when params.sort.field is
// 'symbol' or 'volume24h' — 'id' already falls outside that allowlist, so setting the sort back to
// it is indistinguishable, on the wire, from never having sorted at all. Reusing it as the
// "cleared" state (rather than inventing a second "no sort" representation) means the third click
// below rides the exact mechanism dataProvider.test.ts already covers.
export const CLEARED_SORT: SortPayload = { field: 'id', order: 'ASC' };

interface SortableHeaderCellProps {
  source: string;
  field: ReactElement;
  sort?: SortPayload;
  setSort?: (sort: SortPayload) => void;
}

// A header cell for one of the two real-sort columns. Renders its own TableSortLabel (instead of
// going through react-admin's DatagridHeaderCell) for two reasons:
//   1. DatagridHeaderCell hides the sort icon entirely while inactive (see
//      DatagridHeaderCell.js — "Remove the sort icons when not active"), so there is no supported
//      prop to make an inactive column advertise sortability.
//   2. Its click handler (in DatagridHeader.js) only ever toggles ASC <-> DESC on the active
//      column and its tooltip always names the opposite of the current order — neither has an
//      override point for a third "clear" state.
// The click handling and tooltip text below implement the ASC -> DESC -> cleared cycle themselves,
// still driving the same `setSort` react-admin gives every Datagrid header.
function SortableHeaderCell({ source, field, sort, setSort }: SortableHeaderCellProps) {
  const translate = useTranslate();
  const active = sort?.field === source;
  const order = active ? sort?.order : undefined;
  const columnLabel = typeof field.props.label === 'string' ? field.props.label : source;

  const nextSort: SortPayload = !active
    ? { field: source, order: 'ASC' }
    : order === 'ASC'
      ? { field: source, order: 'DESC' }
      : CLEARED_SORT;

  const tooltipKey = !active
    ? 'module.perpsV1Symbols.sort.ascending'
    : order === 'ASC'
      ? 'module.perpsV1Symbols.sort.descending'
      : 'module.perpsV1Symbols.sort.clear';

  const handleClick = () => setSort?.(nextSort);

  return (
    <TableCell variant="head" className={clsx(DatagridClasses.headerCell, `column-${source}`)}>
      <Tooltip
        title={translate(tooltipKey, { field: columnLabel })}
        enterDelay={300}
        placement="bottom-start"
      >
        <TableSortLabel
          active={active}
          direction={active && order === 'DESC' ? 'desc' : 'asc'}
          IconComponent={active ? ArrowDownwardIcon : UnfoldMoreIcon}
          onClick={handleClick}
          data-testid={`sort-label-${source}`}
          sx={{
            // react-admin's DatagridHeaderCell (used elsewhere in the app for other Datagrids)
            // sets `.MuiTableSortLabel-icon { display: none }` unless `.Mui-active`; MUI's own
            // default additionally keeps the icon at opacity 0 outside hover/active. Neither
            // leaves any resting-state hint that a column is sortable. Force a low-but-visible
            // opacity at rest so these two columns read as "you can sort this" before the first
            // click, while still reading as visibly duller than the active state.
            '& .MuiTableSortLabel-icon': {
              opacity: active ? 1 : 0.5,
            },
          }}
        >
          {columnLabel}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );
}

// Header cell for the six sortable={false} columns — the same content react-admin's own
// DatagridHeaderCell renders for a non-sortable field (a bare FieldTitle, no TableSortLabel). Built
// locally with the app's own top-level `@mui/material` rather than importing DatagridHeaderCell:
// react-admin bundles its own pinned, nested `@mui/material` install (see
// node_modules/react-admin/node_modules/@mui/material), a *separate* module instance from the
// app's own. Mixing that component's TableCell into a tree rooted at our own <TableHead> breaks
// silently — MUI's <th>/<td> auto-detection depends on a React Context written by <TableHead> and
// read by <TableCell>, and the two don't recognize each other's Context across module instances,
// so the cell falls back to <td>, dropping header semantics (scope="col") for sighted-but-assistive
// navigation. Rendering every cell here from one consistent MUI instance avoids that trap
// entirely — the same reason src/pages/admin/components/XDatagrid rebuilds its own header cell
// rather than reusing react-admin's.
function PlainHeaderCell({ field }: { field: ReactElement }) {
  const resource = useResourceContext();
  const source = field.props.source as string | undefined;
  return (
    <TableCell variant="head" className={clsx(DatagridClasses.headerCell, `column-${source ?? ''}`)}>
      <FieldTitle label={field.props.label} source={source} resource={resource} />
    </TableCell>
  );
}

export interface SortableDatagridHeaderProps {
  children?: ReactNode;
  className?: string;
  sort?: SortPayload;
  setSort?: (sort: SortPayload) => void;
}

// Drop-in replacement for react-admin's default Datagrid header (pass as `<Datagrid header={...}>`
// — a documented Datagrid prop, see Datagrid.d.ts), scoped to what this screen actually needs: no
// row-select checkbox column (bulkActionButtons={false}) and no expand column, so it skips the
// parts of react-admin's DatagridHeader.js that render those. Only the two sortable columns get
// bespoke behavior (SortableHeaderCell above); the other six render identically to before
// (PlainHeaderCell above), just assembled from the same MUI instance as everything else in this
// header — see PlainHeaderCell for why that matters.
export function SortableDatagridHeader({ children, className, ...props }: SortableDatagridHeaderProps) {
  // useListContextWithProps's return type doesn't carry `children`/`className` (they aren't part
  // of ListContext, ra-core types it as Partial<ListControllerResult>) — read those two straight
  // off our own typed props instead and only pull sort/setSort, the pieces that actually live in
  // list context, through the hook.
  const { sort, setSort } = useListContextWithProps(props);

  return (
    <TableHead className={clsx(className, DatagridClasses.thead)}>
      <TableRow className={clsx(DatagridClasses.row, DatagridClasses.headerRow)}>
        {Children.map(children, (field, index) => {
          if (!isValidElement(field)) return null;
          const source = field.props.source as string | undefined;
          if (source && SORTABLE_HEADER_SOURCES.has(source)) {
            return (
              <SortableHeaderCell key={source} source={source} field={field} sort={sort} setSort={setSort} />
            );
          }
          return <PlainHeaderCell key={source || index} field={field} />;
        })}
      </TableRow>
    </TableHead>
  );
}
